import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import * as acp from "@agentclientprotocol/sdk";
import {
  AvatarChat,
  type AvatarChatConfig,
  type AvatarChatHandle,
} from "@pineseed/gui/component/AvatarDome";
import { MessageWithTags, Message, MessageReactions, MessageReaction } from "@pineseed/gui/component";
import { useReactions } from "@pineseed/gui/hook/useReactions";
import { ConnectionProvider, useConnectionContext } from "@pineseed/interface-gui/context/connection";
import { ChatBubbleComposer } from "@pineseed/interface-gui/component/ChatBubbleComposer";
import {
  useAcpChat,
  type AcpCustomTool,
  type UseAcpChatReturn,
} from "@pineseed/interface-gui/hook/useAcpChat";

export interface AcpAvatarChatConfig {
  httpUrl: string;
  wsUrl: string;
  location: string;
  loadSessionTimeoutMs: number;
  uploadTtlS: number;
  reactionEmojis: string[];
  closeOnSendDelayMs: number;
  avatarChat: AvatarChatConfig;
}

export interface AcpAvatarChatProps {
  config: AcpAvatarChatConfig;
  workspaceKey: string;
  isAuthenticated: boolean;
  onReady?: (handle: AvatarChatHandle) => void;
}

// ─── Internal context: feeds bubble content rendered inside AvatarChat ──────

interface AcpChatContextValue {
  usingAcpChat: UseAcpChatReturn;
  reactionEmojis: string[];
  workspaceKey: string;
}

const AcpChatContext = createContext<AcpChatContextValue | null>(null);

function useAcpChatContext(): AcpChatContextValue {
  const ctx = useContext(AcpChatContext);
  if (!ctx) throw new Error("useAcpChatContext must be used within AcpAvatarChat");
  return ctx;
}

// ─── Composer content ────────────────────────────────────────────────────────

function ComposerContent() {
  const { usingAcpChat } = useAcpChatContext();
  const { isConnected } = useConnectionContext();

  return (
    <div className="flex flex-col gap-2">
      <ChatBubbleComposer
        onKeyDown={usingAcpChat.onKeyDown}
        onEditorReady={usingAcpChat.onEditorReady}
        onTextChange={usingAcpChat.onTextChange}
        handleSubmit={usingAcpChat.handleSubmit}
        hasText={usingAcpChat.hasText}
        isConnected={isConnected}
        isAuthenticated={usingAcpChat.isAuthenticated}
        inputActionHandlers={usingAcpChat.inputActionHandlers}
      />
    </div>
  );
}

// ─── ACP agent message content ───────────────────────────────────────────────

type AcpAgentMessageContentProps = {
  content: string;
  messageId: string | null;
  threadId: string;
};

function AcpAgentMessageContent({ content, messageId, threadId }: AcpAgentMessageContentProps) {
  const { usingAcpChat, reactionEmojis, workspaceKey } = useAcpChatContext();

  if (!messageId) {
    return <div className="pt-4">Loading...</div>;
  }

  return (
    <AcpAgentMessageBody
      content={content}
      messageId={messageId}
      threadId={threadId}
      usingAcpChat={usingAcpChat}
      reactionEmojis={reactionEmojis}
      workspaceKey={workspaceKey}
    />
  );
}

function AcpAgentMessageBody({
  content,
  messageId,
  threadId,
  usingAcpChat,
  reactionEmojis,
  workspaceKey,
}: {
  content: string;
  messageId: string;
  threadId: string;
  usingAcpChat: UseAcpChatReturn;
  reactionEmojis: string[];
  workspaceKey: string;
}) {
  const { parseTextWithTags, extractTagNameForDisplay, participantId, httpUrl, wsUrl } = usingAcpChat;

  const { reactions, addReaction, removeReaction } = useReactions({
    participantId: participantId?.id || "",
    messageId,
    threadId,
    httpUrl,
    wsUrl,
    extraHeaders: { "X-Workspace-Key": workspaceKey },
  });

  return (
    <div className="flex flex-col gap-2">
      <Message>
        <MessageWithTags
          content={content}
          markdown={true}
          useTags={() => ({ parseTextWithTags, extractTagNameForDisplay })}
        />
      </Message>
      <MessageReactions
        className="flex justify-center"
        messageId={messageId}
        participantId={participantId?.id || ""}
        displayMode="expanded"
        reactions={reactions}
        onReact={(emoji: string) => {
          const reaction: MessageReaction = {
            emoji,
            participantId: participantId?.id || "",
            messageId,
          };
          addReaction(reaction);
        }}
        onUnreact={(emoji: string) => {
          removeReaction(emoji);
        }}
        emojis={reactionEmojis}
        showCount={false}
      />
    </div>
  );
}

// ─── Composite ───────────────────────────────────────────────────────────────

export function AcpAvatarChat(props: AcpAvatarChatProps) {
  if (!props.workspaceKey) return null;
  return <AcpAvatarChatInner {...props} />;
}

function AcpAvatarChatInner({ config, workspaceKey, isAuthenticated, onReady }: AcpAvatarChatProps) {
  const avatarChatRef = useRef<AvatarChatHandle>(null);

  const handle = useCallback((): AvatarChatHandle => {
    const current = avatarChatRef.current;
    if (!current) throw new Error("[AcpAvatarChat] AvatarChat handle not mounted");
    return current;
  }, []);

  // Accumulate agent message chunks per session, keyed by messageId.
  // A new messageId resets the buffer — each turn produces a distinct message.
  const acpChunksRef = useRef<Record<string, { messageId: string | null; text: string }>>({});

  const onAcpSessionUpdate = useCallback(
    (sessionId: string, update: acp.SessionUpdate) => {
      if (update.sessionUpdate !== "agent_message_chunk") return;

      const chunk = update as acp.SessionUpdate & {
        sessionUpdate: "agent_message_chunk";
        messageId?: string | null;
      };
      const text =
        chunk.content.type === "text"
          ? (chunk.content as { type: "text"; text: string }).text
          : "";
      if (!text) return;

      const incomingMessageId = chunk.messageId ?? null;
      const current = acpChunksRef.current[sessionId];
      acpChunksRef.current[sessionId] =
        current?.messageId === incomingMessageId
          ? { messageId: incomingMessageId, text: current.text + text }
          : { messageId: incomingMessageId, text };

      const accumulated = acpChunksRef.current[sessionId].text;

      handle().show(
        <AcpAgentMessageContent content={accumulated} messageId={incomingMessageId} threadId={sessionId} />,
      );
    },
    [handle],
  );

  const onSubmitted = useCallback(
    async (content: string) => {
      console.log("AcpAvatarChat: Message send event received:", content);
      setTimeout(() => {
        handle().close();
      }, config.closeOnSendDelayMs);
    },
    [handle, config.closeOnSendDelayMs],
  );

  const onPromptLifecycle = useCallback(() => {
    handle().shockwave();
  }, [handle]);

  const customTools = useMemo<AcpCustomTool[]>(
    () => [
      {
        definition: {
          name: "emoji_reaction",
          description:
            "Triggers an emoji burst animation on the page. Call this to celebrate, react emotionally, or add visual flair to a response.",
          tags: ["ui", "animation"],
          category: "browser",
          version: "1.0.0",
          timeoutSeconds: 5,
          exclusive: false,
          estimatedCostTokens: 0,
          requiresCredentials: false,
          provider: "browser",
          credentialKey: "",
          embedding: [],
          executionMode: 0,
          inputSchema: {
            type: "object",
            properties: {
              emoji: {
                type: "string",
                description: "Emoji or sequence of emojis to burst on screen.",
              },
            },
            required: ["emoji"],
          },
        },
        handle: (args) => {
          const emoji = String(args["emoji"] ?? "");
          handle().burst(emoji);
          return { ok: true, emoji };
        },
      },
    ],
    [handle],
  );

  const usingAcpChat = useAcpChat({
    httpUrl: config.httpUrl,
    wsUrl: config.wsUrl,
    acpWsUrl: `${config.wsUrl}/acp`,
    workspaceKey,
    isAuthenticated,
    location: config.location,
    acpLoadSessionTimeoutMs: config.loadSessionTimeoutMs,
    acpUploadTtlS: config.uploadTtlS,
    customTools,
    onAcpSessionUpdate,
    onSubmitted,
    onPromptLifecycle,
  });

  const onAvatarClick = useCallback((event: MouseEvent) => {
    console.log("AcpAvatarChat: handleClickEvent called", event);
    handle().show(<ComposerContent />);
  }, [handle]);

  useEffect(() => {
    if (onReady) onReady(handle());
    // Mount-only: the handle is set once AvatarChat mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: AcpChatContextValue = {
    usingAcpChat,
    reactionEmojis: config.reactionEmojis,
    workspaceKey,
  };

  return (
    <ConnectionProvider config={usingAcpChat.connectionConfig}>
      <AcpChatContext.Provider value={contextValue}>
        <AvatarChat ref={avatarChatRef} config={config.avatarChat} onAvatarClick={onAvatarClick} />
      </AcpChatContext.Provider>
    </ConnectionProvider>
  );
}
